import React from 'react';
import { NavLink } from 'react-router-dom';

import { c } from 'ttag';

import { SidebarAccount, SidebarContext, useAuthentication, useLocalState } from '@freelancelabs/components';
import {
    BriefCaseOutline,
    ChevronDoubleLeft,
    ChevronDoubleRight,
    LogoutOutline,
    MemoOutline,
    PeopleOutline,
    SettingsOutline,
    UserCircleOutline,
    UserSolid,
} from '@freelancelabs/icons';
import { Stage, getStage } from '@freelancelabs/shared';
import { clsx } from '@freelancelabs/utils';

interface Entry {
    to: string;
    label: string;
    icon: React.ReactNode;
}

interface Props {
    /** Name of the signed-in person, from `viewer`. */
    name: string;
    /** Their role on Freelance.com: « Administrateur » or « Collaborateur ». */
    roleName: string;
}

/**
 * First letter of the first two words. Falls back to the first two characters when
 * there is a single word, and to nothing at all rather than a stray letter when the
 * name is empty: `viewer` exposes `firstName` and `lastName` as nullable.
 */
const initialsOf = (name: string) => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
        return '';
    }
    if (words.length === 1) {
        return words[0].slice(0, 2).toUpperCase();
    }
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

/**
 * Persistent navigation of the back-office. Dark, to distinguish an internal console
 * from the portal at a glance. An entry appears under « À venir » (upcoming) until its
 * screen exists, rather than linking to an empty page.
 */
const Rail = ({ name, roleName }: Props) => {
    const { logout } = useAuthentication();
    // Collapsed rail, persisted across visits. The storage key must differ from the
    // portal's: the two apps share `localhost` in dev, where a shared key would collapse
    // one when collapsing the other.
    const [isShrink, setIsShrink] = useLocalState(false, 'ui-bo-rail-shrink');
    // getStage() reads the hostname; in production it returns `prod`, where naming the
    // environment would be noise.
    const stage = getStage();

    const available: Entry[] = [
        { to: '/comptes', label: c('Back-office nav').t`Accounts`, icon: <BriefCaseOutline size={16} /> },
        { to: '/utilisateurs', label: c('Back-office nav').t`Users`, icon: <UserSolid size={16} /> },
        { to: '/roles', label: c('Back-office nav').t`Roles`, icon: <SettingsOutline size={16} /> },
        // A referential of the platform, unlike the dashboard catalogue, which lives on
        // the account it belongs to.
        {
            to: '/listes-de-partenaires',
            label: c('Back-office nav').t`Partner lists`,
            icon: <PeopleOutline size={16} />,
        },
        { to: '/journal', label: c('Back-office nav').t`Activity log`, icon: <MemoOutline size={16} /> },
    ];

    const upcoming: Entry[] = [];

    return (
        <aside
            className={clsx(
                'shrink-0 bg-rail text-rail-text flex flex-col sticky top-0 h-screen transition-[width]',
                isShrink ? 'w-rail-shrink' : 'w-rail'
            )}
        >
            <div className={clsx('border-b border-rail-hover', isShrink ? 'px-2 pt-5 pb-4' : 'px-4 pt-5 pb-4')}>
                {/* The title hides when collapsed rather than truncating to nothing. */}
                {!isShrink && (
                    <p className="font-semibold text-sm tracking-tight">
                        {c('Back-office').t`Client Portal · Back-office`}
                    </p>
                )}
                <button
                    type="button"
                    onClick={() => setIsShrink(!isShrink)}
                    aria-expanded={!isShrink}
                    aria-label={
                        isShrink ? c('Back-office nav').t`Expand the menu` : c('Back-office nav').t`Collapse the menu`
                    }
                    title={
                        isShrink ? c('Back-office nav').t`Expand the menu` : c('Back-office nav').t`Collapse the menu`
                    }
                    className={clsx(
                        'flex items-center justify-center rounded-md p-1.5 text-rail-muted hover:bg-rail-hover hover:text-rail-text',
                        isShrink ? 'mx-auto' : 'ml-auto -mt-6'
                    )}
                >
                    {isShrink ? <ChevronDoubleRight size={14} /> : <ChevronDoubleLeft size={14} />}
                </button>
                {stage !== Stage.PROD && (
                    <span className="mt-1.5 inline-block font-mono text-[11px] text-rail-muted border border-rail-hover border-l-2 border-l-admin-accent px-1.5 py-0.5 rounded-sm">
                        {/* Collapsed: the stage alone, the label would not fit. */}
                        {isShrink ? (
                            stage
                        ) : (
                            <>
                                {c('Back-office').t`Environment`} {stage}
                            </>
                        )}
                    </span>
                )}
            </div>

            <nav className="flex-1 px-2 py-3" aria-label={c('Back-office nav').t`Administration`}>
                {!isShrink && (
                    <p className="px-2 pt-2 pb-1.5 text-[10.5px] uppercase tracking-[0.09em] text-rail-muted">
                        {c('Back-office nav').t`Administration`}
                    </p>
                )}
                {available.map(({ to, label, icon }) => (
                    <NavLink
                        key={to}
                        to={to}
                        title={isShrink ? label : undefined}
                        aria-label={isShrink ? label : undefined}
                        className={({ isActive }) =>
                            clsx(
                                'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm no-underline text-rail-text hover:bg-rail-hover',
                                isShrink && 'justify-center',
                                isActive && 'bg-rail-hover font-medium shadow-[inset_2px_0_0_var(--admin-accent)]'
                            )
                        }
                    >
                        {icon}
                        {!isShrink && label}
                    </NavLink>
                ))}

                {!isShrink && upcoming.length > 0 && (
                    <p className="px-2 pt-4 pb-1.5 text-[10.5px] uppercase tracking-[0.09em] text-rail-muted">
                        {c('Back-office nav').t`Coming soon`}
                    </p>
                )}
                {upcoming.map(({ to, label, icon }) => (
                    // A span and not a disabled link: a screen reader announces a link
                    // it can follow, and this one cannot be followed.
                    <span
                        key={to}
                        // Collapsed, the hover must carry both name and state, else a greyed
                        // icon reads as broken rather than upcoming.
                        title={isShrink ? `${label} · ${c('Back-office nav').t`soon`}` : undefined}
                        aria-label={isShrink ? `${label} · ${c('Back-office nav').t`soon`}` : undefined}
                        className={clsx(
                            'flex items-center gap-2.5 px-2.5 py-2 text-sm text-rail-muted cursor-not-allowed',
                            isShrink && 'justify-center'
                        )}
                    >
                        {icon}
                        {!isShrink && (
                            <>
                                {label}
                                <span className="ml-auto font-mono text-[10px]">{c('Back-office nav').t`soon`}</span>
                            </>
                        )}
                    </span>
                ))}
            </nav>

            {/* The same block as the portal, only colors change via the `--dark` variant.
                `roleName` takes the place of the portal's email. The context, not a
                class, is what `SidebarAccount` reads to stop rendering the name and
                chevron when collapsed. */}
            <SidebarContext.Provider value={{ isShrink }}>
                <div className={clsx('border-t border-rail-hover', isShrink ? 'p-2' : 'p-3')}>
                    <SidebarAccount
                        className="sidebar-account--dark"
                        name={name}
                        email={roleName}
                        initials={initialsOf(name)}
                    >
                        {/* Conditional: without `PUBLIC_ACCOUNT_URL` the link is not
                        rendered rather than leading to a page that does not exist.
                        `?from` names the origin so `account`'s return link brings an admin
                        back here, not to the client portal. */}
                        {import.meta.env.PUBLIC_ACCOUNT_URL && (
                            <a href={`${import.meta.env.PUBLIC_ACCOUNT_URL}/account?from=hub-admin`}>
                                <UserCircleOutline />
                                {c('Back-office nav').t`My account`}
                            </a>
                        )}
                        <hr />
                        <a
                            href="#logout"
                            onClick={(event) => {
                                event.preventDefault();
                                void logout();
                            }}
                        >
                            <LogoutOutline />
                            {c('Back-office nav').t`Logout`}
                        </a>
                    </SidebarAccount>
                </div>
            </SidebarContext.Provider>
        </aside>
    );
};

export default Rail;
